/**
 * installFirst — a phone gets Karr onto its home screen BEFORE signing up.
 *
 * Founder, 4 Oct 2026: *"instead of telling users to add to home screen why
 * not send them a link first where they add to home screen or install and then
 * signup?"* Signing up in the browser and installing afterwards costs a phone
 * twice: an iPhone's home-screen app keeps its own storage apart from Safari,
 * so the icon opens signed out (see installed.ts), and an iPhone takes
 * notifications only from the icon. Installing first, then signing up inside
 * the icon, does it once.
 *
 * Offered, never a gate: "Continue in the browser" is always one tap away, and
 * a laptop never sees the step at all.
 *
 * @since installfirst
 */

export interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let held: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/**
 * Chrome offers the install once, early in the page load, often before the
 * lazy sign-up screen or the lazy fill rail has drawn. Call on app load so the
 * offer is kept for whichever asks: the sign-up screen (InstallFirst) or the
 * rail's "Put Karr on your home screen" row (useInstallPrompt).
 *
 * deskmode (5 Oct 2026): this used to keep it on the sign-up screen only, and
 * leave the rail to catch it with a listener of its own. The rail is lazy and
 * loads after sign-in, so on a fast phone the offer had come and gone before
 * it listened, and a signed-in phone was never offered the install at all.
 */
export function captureInstallPrompt(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        held = e as BeforeInstallPromptEvent;
        notify();
    });
    window.addEventListener('appinstalled', () => {
        held = null;
        notify();
    });
}

export function heldInstallPrompt(): BeforeInstallPromptEvent | null {
    return held;
}

export function onInstallPromptChange(fn: () => void): () => void {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
}

/** Hand the kept offer over once; it works only once. */
export function takeHeldInstallPrompt(): BeforeInstallPromptEvent | null {
    const e = held;
    held = null;
    if (e) notify();
    return e;
}

export const SIGNUP_AFTER_INSTALL = 'karr_signup_after_install';

/** Where a signed-out home-screen icon opens. */
export function standaloneSignedOutRoute(): '/auth/signup' | '/auth/signin' {
    try {
        if (localStorage.getItem(SIGNUP_AFTER_INSTALL) === '1') return '/auth/signup';
    } catch { /* storage blocked */ }
    return '/auth/signin';
}

/** A session exists: the install-then-sign-up trip is over. */
export function clearSignupAfterInstall(): void {
    try { localStorage.removeItem(SIGNUP_AFTER_INSTALL); } catch { /* storage blocked */ }
}
